import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, Gamepad2, Heart, Calendar, MessageCircle, Phone, Settings, ChevronRight, X, Check, Lightbulb, Plus, MoreVertical } from "lucide-react";
import { useTranslation } from "react-i18next";

// ─── Types ───────────────────────────────────────────────────────────────────

interface Notification {
    id: number;
    app: string;
    appIcon: React.ReactNode;
    message: string;
    time: string;
    color: string;
    read: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

const SmartNotificationsWidget: React.FC = () => {
    const { t } = useTranslation("nonHealth");

    const initialNotifications: Notification[] = useMemo(() => [
        {
            id: 1,
            app: t("widgets.smartNotifications.apps.health"),
            appIcon: <Heart className="w-3.5 h-3.5" />,
            message: t("widgets.smartNotifications.notifications.healthMsg"),
            time: t("widgets.smartNotifications.time.justNow"),
            color: "from-rose-500 to-pink-600",
            read: false,
        },
        {
            id: 2,
            app: t("widgets.smartNotifications.apps.calendar"),
            appIcon: <Calendar className="w-3.5 h-3.5" />,
            message: t("widgets.smartNotifications.notifications.calendarMsg"),
            time: t("widgets.smartNotifications.time.minutesAgo", { count: 2 }),
            color: "from-violet-500 to-purple-600",
            read: false,
        },
        {
            id: 3,
            app: t("widgets.smartNotifications.apps.game"),
            appIcon: <Gamepad2 className="w-3.5 h-3.5" />,
            message: t("widgets.smartNotifications.notifications.gameMsg"),
            time: t("widgets.smartNotifications.time.minutesAgo", { count: 5 }),
            color: "from-fuchsia-500 to-purple-600",
            read: true,
        },
        {
            id: 4,
            app: t("widgets.smartNotifications.apps.message"),
            appIcon: <MessageCircle className="w-3.5 h-3.5" />,
            message: t("widgets.smartNotifications.notifications.messageMsg"),
            time: t("widgets.smartNotifications.time.minutesAgo", { count: 10 }),
            color: "from-blue-500 to-indigo-600",
            read: true,
        },
    ], [t]);

    const allowedApps = useMemo(() => [
        { name: t("widgets.smartNotifications.apps.call"), icon: <Phone className="w-4 h-4" />, enabled: true, color: "bg-green-100 text-green-600 border-green-200" },
        { name: t("widgets.smartNotifications.apps.message"), icon: <MessageCircle className="w-4 h-4" />, enabled: true, color: "bg-blue-100 text-blue-600 border-blue-200" },
        { name: t("widgets.smartNotifications.apps.health"), icon: <Heart className="w-4 h-4" />, enabled: true, color: "bg-rose-100 text-rose-600 border-rose-200" },
        { name: t("widgets.smartNotifications.apps.calendar"), icon: <Calendar className="w-4 h-4" />, enabled: true, color: "bg-violet-100 text-violet-600 border-violet-200" },
        { name: t("widgets.smartNotifications.apps.game"), icon: <Gamepad2 className="w-4 h-4" />, enabled: false, color: "bg-gray-100 text-gray-400 border-gray-200" },
    ], [t]);

    const [notifications, setNotifications] = useState<Notification[]>(initialNotifications);
    const [activeView, setActiveView] = useState<"list" | "settings">("list");

    const unreadCount = notifications.filter((n) => !n.read).length;

    const dismissNotification = (id: number) => {
        setNotifications((prev) => prev.filter((n) => n.id !== id));
    };

    const markAllRead = () => {
        setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    };

    return (
        <motion.div
            key="smartNotifications-widget"
            className="relative w-full max-w-sm"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
        >
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-fuchsia-200/60 relative overflow-hidden">
                {/* ── Background blobs ───────────────────────────────────────── */}
                <motion.div
                    className="absolute -right-10 -top-10 w-32 h-32 bg-fuchsia-500/10 rounded-full blur-2xl"
                    animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.7, 0.5] }}
                    transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
                />
                <motion.div
                    className="absolute -left-10 -bottom-10 w-32 h-32 bg-purple-500/10 rounded-full blur-2xl"
                    animate={{ scale: [1.2, 1, 1.2], opacity: [0.5, 0.7, 0.5] }}
                    transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                />

                {/* ── Progress bar (top) ─────────────────────────────────────── */}
                <motion.div
                    className="absolute top-0 left-0 h-1 bg-gradient-to-r from-fuchsia-400 to-purple-600"
                    initial={{ width: "0%" }}
                    animate={{ width: "100%" }}
                    transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
                />

                {/* ── Header ─────────────────────────────────────────────────── */}
                <div className="flex justify-between items-center mb-3">
                    <div className="flex items-center gap-2">
                        {/* Bell with badge */}
                        <motion.div className="relative" animate={{ rotate: [0, 0, 8, -8, 8, -8, 0] }} transition={{ duration: 3, repeat: Infinity, repeatDelay: 2 }}>
                            <div className="w-8 h-8 bg-gradient-to-br from-fuchsia-500 to-purple-600 rounded-full flex items-center justify-center shadow-md">
                                <Bell className="w-4 h-4 text-white" />
                            </div>
                            {unreadCount > 0 && (
                                <motion.span
                                    className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center shadow-sm"
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{ type: "spring", stiffness: 300 }}
                                >
                                    {unreadCount}
                                </motion.span>
                            )}
                        </motion.div>
                        <h5 className="text-purple-900 font-bold text-lg">{t("widgets.smartNotifications.title")}</h5>
                    </div>

                    {/* View toggle */}
                    <div className="flex items-center gap-1.5">
                        {unreadCount > 0 && (
                            <motion.button
                                className="text-xs text-fuchsia-600 bg-fuchsia-50 border border-fuchsia-200 px-2 py-1 rounded-full"
                                onClick={markAllRead}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                {t("widgets.smartNotifications.readAll")}
                            </motion.button>
                        )}
                        <motion.button
                            className={`w-7 h-7 rounded-full flex items-center justify-center border ${
                                activeView === "settings" ? "bg-fuchsia-100 border-fuchsia-300 text-fuchsia-600" : "bg-gray-100 border-gray-200 text-gray-500"
                            }`}
                            onClick={() => setActiveView((v) => (v === "list" ? "settings" : "list"))}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                        >
                            <Settings className="w-3.5 h-3.5" />
                        </motion.button>
                    </div>
                </div>

                {/* ── Main content (animated switch between list / settings) ─── */}
                <AnimatePresence mode="wait">
                    {activeView === "list" ? (
                        <motion.div key="notifications-list" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>
                            {/* Notification cards */}
                            <div className="space-y-2 mb-3">
                                <AnimatePresence>
                                    {notifications.length === 0 ? (
                                        <motion.div className="flex flex-col items-center justify-center py-8 text-gray-400" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                                            <Bell className="w-10 h-10 mb-2 opacity-30" />
                                            <p className="text-sm">{t("widgets.smartNotifications.empty")}</p>
                                        </motion.div>
                                    ) : (
                                        notifications.map((notif, index) => (
                                            <motion.div
                                                key={notif.id}
                                                className={`relative flex items-start gap-2.5 p-2.5 rounded-xl border transition-all ${
                                                    notif.read ? "bg-white/50 border-gray-100" : "bg-white/80 border-fuchsia-100 shadow-sm"
                                                }`}
                                                initial={{ opacity: 0, x: 20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                exit={{ opacity: 0, x: -30, height: 0, marginBottom: 0 }}
                                                transition={{ duration: 0.3, delay: index * 0.05 }}
                                                whileHover={{ y: -2, boxShadow: "0 4px 12px rgba(168,85,247,0.1)" }}
                                                layout
                                            >
                                                {/* Unread indicator */}
                                                {!notif.read && (
                                                    <motion.span
                                                        className="absolute top-2.5 right-2.5 w-1.5 h-1.5 rounded-full bg-fuchsia-500"
                                                        animate={{ opacity: [1, 0.4, 1] }}
                                                        transition={{ duration: 1.5, repeat: Infinity }}
                                                    />
                                                )}

                                                {/* App icon */}
                                                <div className={`shrink-0 w-7 h-7 rounded-full bg-gradient-to-br ${notif.color} flex items-center justify-center text-white shadow-sm`}>
                                                    {notif.appIcon}
                                                </div>

                                                {/* Content */}
                                                <div className="flex-1 min-w-0 pr-2">
                                                    <div className="flex justify-between items-center mb-0.5">
                                                        <span className="text-xs font-bold text-gray-700">{notif.app}</span>
                                                        <span className="text-[10px] text-gray-400">{notif.time}</span>
                                                    </div>
                                                    <p className="text-xs text-gray-600 leading-relaxed line-clamp-2">{notif.message}</p>
                                                </div>

                                                {/* Dismiss button */}
                                                <motion.button
                                                    className="shrink-0 w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center text-gray-400"
                                                    onClick={() => dismissNotification(notif.id)}
                                                    whileHover={{ scale: 1.2, backgroundColor: "#fce7f3", color: "#ec4899" }}
                                                    whileTap={{ scale: 0.9 }}
                                                >
                                                    <X className="w-3 h-3" />
                                                </motion.button>
                                            </motion.div>
                                        ))
                                    )}
                                </AnimatePresence>
                            </div>

                            {/* Stats row */}
                            <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
                                <motion.div className="bg-fuchsia-50 border border-fuchsia-100 p-2.5 rounded-lg flex items-center gap-2" whileHover={{ scale: 1.02, y: -1 }}>
                                    <div className="w-6 h-6 bg-fuchsia-100 rounded-full flex items-center justify-center text-fuchsia-600">
                                        <Bell className="w-3.5 h-3.5" />
                                    </div>
                                    <div>
                                        <div className="text-fuchsia-700 font-medium">{t("widgets.smartNotifications.unread")}</div>
                                        <motion.div
                                            className="font-bold text-fuchsia-900 text-base"
                                            animate={{ scale: unreadCount > 0 ? [1, 1.1, 1] : 1 }}
                                            transition={{ duration: 1, repeat: unreadCount > 0 ? Infinity : 0 }}
                                        >
                                            {unreadCount}
                                        </motion.div>
                                    </div>
                                </motion.div>

                                <motion.div className="bg-purple-50 border border-purple-100 p-2.5 rounded-lg flex items-center gap-2" whileHover={{ scale: 1.02, y: -1 }}>
                                    <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center text-purple-600">
                                        <Check className="w-3.5 h-3.5" />
                                    </div>
                                    <div>
                                        <div className="text-purple-700 font-medium">{t("widgets.smartNotifications.totalToday")}</div>
                                        <div className="font-bold text-purple-900 text-base">{notifications.length}</div>
                                    </div>
                                </motion.div>
                            </div>
                        </motion.div>
                    ) : (
                        /* ── Settings view ──────────────────────────────────────── */
                        <motion.div
                            key="notifications-settings"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                            transition={{ duration: 0.3 }}
                            className="mb-3"
                        >
                            <p className="text-xs text-gray-500 mb-2 font-medium">{t("widgets.smartNotifications.allowedApps")}</p>
                            <div className="space-y-2">
                                {allowedApps.map((app, i) => (
                                    <motion.div
                                        key={app.name}
                                        className="flex items-center justify-between p-2 bg-white/60 rounded-lg border border-gray-100"
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: i * 0.05 }}
                                        whileHover={{ y: -1, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
                                    >
                                        <div className="flex items-center gap-2">
                                            <div className={`w-7 h-7 rounded-full border flex items-center justify-center ${app.color}`}>{app.icon}</div>
                                            <span className="text-sm text-gray-700">{app.name}</span>
                                        </div>
                                        {/* Toggle */}
                                        <motion.div
                                            className={`w-10 h-5 rounded-full flex items-center px-0.5 transition-colors ${app.enabled ? "bg-fuchsia-500" : "bg-gray-200"}`}
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                        >
                                            <motion.div
                                                className="w-4 h-4 bg-white rounded-full shadow-sm"
                                                animate={{ x: app.enabled ? 20 : 0 }}
                                                transition={{ type: "spring", stiffness: 500, damping: 30 }}
                                            />
                                        </motion.div>
                                    </motion.div>
                                ))}
                            </div>

                            {/* Add app button */}
                            <motion.button
                                className="w-full mt-2 border border-dashed border-fuchsia-300 text-fuchsia-600 text-xs py-2 rounded-lg flex items-center justify-center gap-1.5"
                                whileHover={{ scale: 1.02, backgroundColor: "rgba(250,232,255,0.5)" }}
                                whileTap={{ scale: 0.98 }}
                            >
                                <Plus className="w-3.5 h-3.5" />
                                {t("widgets.smartNotifications.addApp")}
                            </motion.button>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* ── Tip / info box ─────────────────────────────────────────── */}
                <motion.div
                    className="bg-gradient-to-r from-fuchsia-50 to-purple-50 border border-fuchsia-100 text-fuchsia-700 text-xs p-2.5 rounded-lg flex items-center gap-2 relative overflow-hidden"
                    whileHover={{ scale: 1.01 }}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                >
                    <motion.div
                        className="w-5 h-5 bg-fuchsia-100 rounded-full flex items-center justify-center shrink-0"
                        animate={{ rotate: [0, -10, 10, 0] }}
                        transition={{ duration: 3, repeat: Infinity }}
                    >
                        <Lightbulb className="w-3 h-3" />
                    </motion.div>
                    <span>{activeView === "list" ? t("widgets.smartNotifications.tipList") : t("widgets.smartNotifications.tipSettings")}</span>
                </motion.div>
            </div>
        </motion.div>
    );
};

export default SmartNotificationsWidget;